import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { AIService } from 'src/app/ai/ai.service';

import { FetchSimpleInformationForAllEmployees } from '../../employees/employees.actions';
import { EmployeeState } from '../../employees/employees.state';
import DefaultProjectEmployeeLinkDTO from '../../models/DefaultProjectEmployeeLinkDTO';
import { Employee } from '../../models/Employee';
import Project from '../../models/Project';
import { AddDefaultEmployees } from '../project.actions';

@Component({
    selector: 'app-add-default-linked-employee-modal',
    templateUrl: './add-default-linked-employee-modal.component.html',
    styleUrls: ['./add-default-linked-employee-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class AddDefaultLinkedEmployeeModalComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    @Input() project: Project;
    defaultLinkedEmployeeForm: UntypedFormGroup;

    @Select(EmployeeState.EmployeesSimpleInformation) employees$: Observable<Employee[]>;
    employees: Employee[];
    checkedEmployees: Employee[] = [];
    allCheck = false;
    filterText: string = '';
    aiExtracting = false;

    constructor(
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private store: Store,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private aiService: AIService,
    ) {}

    ngOnInit(): void {
        this.fetchData();
        this.createForm();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private createForm() {
        this.defaultLinkedEmployeeForm = this.fb.group({
            pricePerHour: ['', Validators.required],
            budget: [null],
            effectiveFrom: ['', Validators.required],
            effectiveTo: [''],
            contractFile: [null],
            poNumber: [''],
            sesNumber: [''],
            contractAgreementNumber: [''],
            freeText: [''],
        });
    }

    private fetchData(): void {
        this.subscriptions.add(
            this.employees$.subscribe((employees) => {
                if (!employees) {
                    this.logger.debug('Fetching Employees');
                    this.store.dispatch(new FetchSimpleInformationForAllEmployees());
                    return;
                }

                // Allow adding multiple price entries per employee (do not filter out already linked)
                // Normalize list and sort by lastName, firstName for readability
                this.employees = [...employees].sort((a, b) => {
                    const al = (a.lastName || '').toLowerCase();
                    const bl = (b.lastName || '').toLowerCase();
                    if (al !== bl) return al < bl ? -1 : 1;
                    const af = (a.firstName || '').toLowerCase();
                    const bf = (b.firstName || '').toLowerCase();
                    return af < bf ? -1 : af > bf ? 1 : 0;
                });
            }),
        );
    }

    private readFileAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const res = (reader.result as string) || '';
                const base64 = res.includes('base64,') ? res.split('base64,')[1] : res;
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async extractInvoiceFieldsWithAi(): Promise<void> {
        try {
            this.aiExtracting = true;
            const contractFileValue: any = this.defaultLinkedEmployeeForm.value?.contractFile || null;
            if (!contractFileValue) {
                this.toastr.info('Please upload/select a contract document first.');
                return;
            }

            let source: string;
            if (typeof contractFileValue === 'string') {
                source = contractFileValue;
            } else if (contractFileValue instanceof File) {
                const base64 = await this.readFileAsBase64(contractFileValue);
                const mime = contractFileValue.type || 'application/pdf';
                source = `data:${mime};base64,${base64}`;
            } else {
                this.toastr.error('Unsupported contract file source for AI extraction.');
                return;
            }

            const result = await this.aiService.suggestInvoiceContractFieldsFromPdfUrl(source);
            this.defaultLinkedEmployeeForm.patchValue({
                pricePerHour: result.pricePerHour ?? this.defaultLinkedEmployeeForm.value?.pricePerHour ?? '',
                budget: result.budget ?? this.defaultLinkedEmployeeForm.value?.budget ?? null,
                effectiveFrom: result.effectiveFrom || this.defaultLinkedEmployeeForm.value?.effectiveFrom || '',
                effectiveTo: result.effectiveTo || this.defaultLinkedEmployeeForm.value?.effectiveTo || '',
                poNumber: result.poNumber || this.defaultLinkedEmployeeForm.value?.poNumber || '',
                sesNumber: result.sesNumber || this.defaultLinkedEmployeeForm.value?.sesNumber || '',
                contractAgreementNumber: result.contractAgreementNumber || this.defaultLinkedEmployeeForm.value?.contractAgreementNumber || '',
                freeText: result.freeText || this.defaultLinkedEmployeeForm.value?.freeText || '',
            });

            if (
                !result.pricePerHour &&
                !result.budget &&
                !result.effectiveFrom &&
                !result.effectiveTo &&
                !result.poNumber &&
                !result.sesNumber &&
                !result.contractAgreementNumber &&
                !result.freeText
            ) {
                this.toastr.info('AI found no invoice reference fields in this document.');
            } else {
                this.toastr.success('AI fields extracted and prefilled.');
            }
        } catch (err: any) {
            this.logger.error('AI extraction for default employee link failed', err);
            this.toastr.error(err?.message || 'AI extraction failed.');
        } finally {
            this.aiExtracting = false;
        }
    }

    checkAll(): void {
        if (this.checkedEmployees.length === this.employees.length && this.allCheck) {
            this.checkedEmployees = [];
            this.allCheck = false;
        } else {
            this.checkedEmployees = this.employees;
            this.allCheck = true;
        }
    }

    isChecked(employee: Employee): boolean {
        return this.checkedEmployees.includes(employee);
    }

    check(employee: Employee): void {
        if (this.isChecked(employee)) {
            this.checkedEmployees = this.checkedEmployees.filter((e) => e.uuid !== employee.uuid);
            this.allCheck = false;
        } else {
            this.checkedEmployees.push(employee);
        }
        if (this.checkedEmployees.length === this.employees.length) {
            this.allCheck = true;
        }
    }

    get filteredEmployees(): Employee[] {
        const q = (this.filterText || '').toLowerCase().trim();
        if (!q) return this.employees || [];
        return (this.employees || []).filter((e) => {
            const name = `${e.firstName || ''} ${e.lastName || ''}`.toLowerCase();
            return name.includes(q);
        });
    }

    async addDefaultLinkedEmployee() {
        if (!this.checkedEmployees || this.checkedEmployees.length === 0) {
            this.toastr.info('Please select at least one employee.');
            return;
        }

        const defaultProjectEmployeeLinkDTO: DefaultProjectEmployeeLinkDTO = {
            employeeIds: this.checkedEmployees.map((e) => e.uuid),
            projectId: this.project.uuid,
            pricePerHour: this.defaultLinkedEmployeeForm.value?.pricePerHour,
            budget:
                this.defaultLinkedEmployeeForm.value?.budget !== null && this.defaultLinkedEmployeeForm.value?.budget !== ''
                    ? Number(this.defaultLinkedEmployeeForm.value?.budget)
                    : null,
            effectiveFrom: this.defaultLinkedEmployeeForm.value?.effectiveFrom,
            effectiveTo: this.defaultLinkedEmployeeForm.value?.effectiveTo,
            poNumber: this.defaultLinkedEmployeeForm.value?.poNumber,
            sesNumber: this.defaultLinkedEmployeeForm.value?.sesNumber,
            contractAgreementNumber: this.defaultLinkedEmployeeForm.value?.contractAgreementNumber,
            freeText: this.defaultLinkedEmployeeForm.value?.freeText,
        };

        const contractFileValue: any = this.defaultLinkedEmployeeForm.value?.contractFile || null;
        if (contractFileValue) {
            if (typeof contractFileValue === 'string') {
                defaultProjectEmployeeLinkDTO.contractUrl = contractFileValue;
                try {
                    const parts = contractFileValue.split('/');
                    defaultProjectEmployeeLinkDTO.contractFileName = parts[parts.length - 1];
                } catch (e) {}
            } else if (contractFileValue instanceof File) {
                const base64 = await this.readFileAsBase64(contractFileValue);
                defaultProjectEmployeeLinkDTO.contractFileName = contractFileValue.name;
                defaultProjectEmployeeLinkDTO.contractMimeType = contractFileValue.type || 'application/octet-stream';
                defaultProjectEmployeeLinkDTO.contractBase64 = base64;
            }
        }

        this.subscriptions.add(
            this.store.dispatch(new AddDefaultEmployees(defaultProjectEmployeeLinkDTO)).subscribe(
                () => {
                    this.toastr.success('Consultancy contract successfully added');
                    this.activeModal.close();
                },
                () => {
                    this.logger.error('Failed to add consultancy contract');
                    this.toastr.error('Oops, something went wrong, Please try again later...');
                },
            ),
        );
    }
}
